import React, { CSSProperties, DragEvent, MouseEvent as ReactMouseEvent, useState } from 'react';
import cloneDeep from 'lodash/cloneDeep';
import { GRID_SIZE } from '../Map/Grid';
import { MapShape } from '../../reducers/map/mapReducer';
import patterns from './spellPatterns';
import { shallowEqual, useSelector } from 'react-redux';
import { Rootstate } from '../../store';
import MapElementShaper from '../Map/MapElementShaper';

interface SpellRenderProps {
    shape: MapShape,
    onDragStart?: (e:DragEvent<HTMLDivElement>) => void,
    onClick?: (e: ReactMouseEvent) => void,
    updateShape?: (shape: MapShape) => void
    className?: string,
    style?: CSSProperties,
    alt?: string,
    showHidden?: boolean,
}

const SpellRender = ({
    shape,
    style,
    className,
    onClick,
    onDragStart,
    updateShape,
    showHidden,
}: SpellRenderProps) => {
    const id = shape._id;
    const [resize, setResize] = useState(false);
    const [currentResize, setCurrentResize] = useState<{ x: number, y: number}>();

    const startResize = (e: ReactMouseEvent<Element>) => {
        setResize(true);
        setCurrentResize({ x: shape.width, y: shape.height });
        const onMouseMove = (moveE: MouseEvent) => {
            const dx = moveE.clientX - e.clientX;
            const dy = moveE.clientY - e.clientY;
            const biggestD = Math.abs(dx) > Math.abs(dy)
                ? dx
                : dy;

            setCurrentResize({ x: (shape.width * 5) + biggestD, y: (shape.height * 5) + biggestD });
        };

        window.addEventListener('mousemove', onMouseMove);

        const onMouseUp = (upEvent: MouseEvent) => {
            const newShape = cloneDeep(shape);

            const dx = upEvent.clientX - e.clientX;
            const dy = upEvent.clientY - e.clientY;
            const biggestD = Math.abs(dx) > Math.abs(dy)
                ? dx
                : dy;
            newShape.width += (biggestD / 5);
            newShape.height += (biggestD / 5);
            updateShape?.(newShape);
            setResize(false);
            setCurrentResize(undefined);
            window.removeEventListener('mousemove', onMouseMove);
            window.removeEventListener('mouseup', onMouseUp);
        };

        window.addEventListener('mouseup', onMouseUp);
    };


    const {
        currentLayer,
        selectedElement,
    } = useSelector((state: Rootstate) => ({
        currentLayer: state.map.mainLayer,
        selectedElement: state.map.selectedElement,
    }), shallowEqual);

    const tokenMatchesLayer = (currentLayer === 'dm' && shape.dm) || currentLayer === 'public' && !shape.dm;

    const isSelectedElement = id === selectedElement?._id && selectedElement.type === 'spell';

    const newWidth = resize && isSelectedElement && currentResize
        ? `${currentResize.x}px`
        : style?.width;

    const newHeight = resize && isSelectedElement && currentResize
        ? `${currentResize.y}px`
        : style?.height;

    return tokenMatchesLayer || showHidden ? (
        <div
            className={className}
            style={{
                ...style,
                width: newWidth,
                height: newHeight,
                opacity: (tokenMatchesLayer || isSelectedElement) ? '1' : '0.5',
            }}
            draggable={tokenMatchesLayer || isSelectedElement}
            onDragStart={tokenMatchesLayer ? onDragStart : undefined}
            onClick={tokenMatchesLayer ? onClick : undefined}
        >
            <svg
                width={resize && currentResize ? currentResize.x : (GRID_SIZE * shape.width) / 5}
                height={resize && currentResize ? currentResize.y : (GRID_SIZE * shape.height) / 5}
            >
                {shape.shape === 'circle' ? (
                    <>
                        {patterns['permanentCircle']((resize && currentResize ? currentResize.x / 5 : shape.width) / 2)}
                    </>
                ) : (
                    <>
                        {patterns['permanentCube'](resize && currentResize ? currentResize.x / 5 : shape.width)}
                    </>
                )}
            </svg>
            {isSelectedElement && (
                <MapElementShaper
                    onResizeStart={startResize}
                    dmLayer={shape.dm}
                    onLayerChange={(x) => {
                        const newShape = cloneDeep(shape);
                        newShape.dm = x;
                        updateShape?.(newShape);
                    }}
                />
            )}
        </div>
    ) : null;
};

export default SpellRender;
